import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

/** Heute als JJJJ-MM-TT, aus den LOKALEN Bestandteilen. */
function heute() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const empty = () => ({
  type: 'RECEIPT',
  amount: '',
  description: '',
  category: '',
  reference: '',
  entryDate: heute(),
  /* Das Gegenkonto. Seit ein Kassaeintrag ins Hauptbuch bucht, ist es Pflicht:
     eine Buchung hat zwei Seiten. Es zu raten waere schlimmer als es zu
     verlangen - eine Barausgabe kann Material, Porto, Reisespesen oder eine
     Privatentnahme sein. */
  counterAccountId: '',
});

export function CashEntryModal({ open, onClose, journalId }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  /* Nur bebuchbare Konten. Die Kasse selbst steht als Gegenkonto nicht zur
     Wahl - Kasse an Kasse waere eine Buchung, die nichts aussagt. */
  const kontenQuery = useQuery({
    queryKey: ['finance', 'accounts', 'alle'],
    queryFn: () => financeApi.listAccounts({ limit: 300 }).then((r) => r.items || []),
    enabled: open,
  });
  const konten = (kontenQuery.data || []).filter((k) => k.isActive !== false);

  const save = useMutation({
    mutationFn: (body) => financeApi.postCashEntry(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'cash-journals'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'cash-journal', journalId] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.description.trim() || !form.amount || Number(form.amount) <= 0) { setError(t('cb.error.entryRequired')); return; }
    if (!form.counterAccountId) { setError(t('cb.error.counterAccountRequired')); return; }
    save.mutate({
      journalId,
      type: form.type,
      amount: Number(form.amount),
      description: form.description.trim(),
      category: form.category || null,
      reference: form.reference || null,
      entryDate: form.entryDate || null,
      counterAccountId: form.counterAccountId,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('cb.post.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field" style={{ maxWidth: 180 }}><span className="field-label">{t('cb.col.type')}</span>
            <select className="input" value={form.type} onChange={set('type')}>
              <option value="RECEIPT">{t('cb.type.RECEIPT')}</option>
              <option value="PAYMENT">{t('cb.type.PAYMENT')}</option>
            </select>
          </label>
          <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('cb.col.amount')}</span>
            <input className="input num" type="number" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} required />
          </label>
        </div>
        <label className="field"><span className="field-label">{t('cb.col.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} required />
        </label>
        <div className="field-row">
          <label className="field"><span className="field-label">{t('cb.field.counterAccount')}</span>
            <select className="input" value={form.counterAccountId} onChange={set('counterAccountId')} required>
              <option value="">{t('cb.field.counterAccountPick')}</option>
              {konten.map((k) => (
                <option key={k.id} value={k.id}>{`${k.code} ${k.name}`}</option>
              ))}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 200 }}><span className="field-label">{t('cb.field.entryDate')}</span>
            <input className="input" type="date" value={form.entryDate} onChange={set('entryDate')} required />
          </label>
        </div>
        {/* Was gleich gebucht wird - in Worten, nicht in Kontonummern. */}
        <p className="muted text-sm">
          {form.type === 'RECEIPT' ? t('cb.hint.receipt') : t('cb.hint.payment')}
        </p>
        <div className="field-row">
          <label className="field"><span className="field-label">{t('cb.field.category')}</span>
            <input className="input" value={form.category} onChange={set('category')} />
          </label>
          <label className="field"><span className="field-label">{t('cb.field.reference')}</span>
            <input className="input" value={form.reference} onChange={set('reference')} />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
